import React, { useState } from 'react';
import {
  Box,
  TextField,
  Popover,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
  Button,
  Divider,
} from '@mui/material';
import { DateCalendar, PickersDay, PickersDayProps } from '@mui/x-date-pickers';
import { CalendarToday as CalendarTodayIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { isSameDay, isWithinInterval, isBefore, isAfter, format, addMonths, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';

export interface BlockedDays {
  holidays: string[];     // 'YYYY-MM-DD' — festivos
  non_working: string[];  // 'YYYY-MM-DD' — fines de semana / días no laborables
  pending: string[];      // 'YYYY-MM-DD' — solicitud pendiente de aprobación
  approved: string[];     // 'YYYY-MM-DD' — solicitud aprobada
  rejected: string[];     // 'YYYY-MM-DD' — solicitud rechazada
}

interface DateRangePickerFieldProps {
  startDate: Date | null;
  endDate: Date | null;
  onRangeChange: (start: Date | null, end: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  blockedDays?: BlockedDays;
}

type SelectionStep = 'start' | 'end';

const DateRangePickerField: React.FC<DateRangePickerFieldProps> = ({
  startDate,
  endDate,
  onRangeChange,
  minDate,
  maxDate,
  error = false,
  helperText,
  disabled = false,
  blockedDays,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [step, setStep] = useState<SelectionStep>('start');
  // Draft selection — only committed to parent on "Aplicar"
  const [draftStart, setDraftStart] = useState<Date | null>(null);
  const [draftEnd, setDraftEnd] = useState<Date | null>(null);
  const [leftMonth, setLeftMonth] = useState<Date>(() => {
    const d = startDate ?? new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const open = Boolean(anchorEl);

  const rightMonth = new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1);

  // Build fast lookup sets
  const holidaySet = new Set(blockedDays?.holidays ?? []);
  const nonWorkingSet = new Set(blockedDays?.non_working ?? []);
  const pendingSet = new Set(blockedDays?.pending ?? []);
  const approvedSet = new Set(blockedDays?.approved ?? []);
  const rejectedSet = new Set(blockedDays?.rejected ?? []);

  const toKey = (d: Date) => format(d, 'yyyy-MM-dd');
  const isHoliday = (d: Date) => holidaySet.has(toKey(d));
  const isNonWorking = (d: Date) => nonWorkingSet.has(toKey(d));
  const isPending = (d: Date) => pendingSet.has(toKey(d));
  const isApproved = (d: Date) => approvedSet.has(toKey(d));
  const isRejected = (d: Date) => rejectedSet.has(toKey(d));
  const isRequested = (d: Date) => isPending(d) || isApproved(d) || isRejected(d);
  const isBlocked = (d: Date) => isHoliday(d) || isNonWorking(d) || isRequested(d);

  const getBlockedTooltip = (d: Date): string => {
    if (isApproved(d)) return 'Solicitud aprobada';
    if (isPending(d)) return 'Solicitud pendiente de aprobación';
    if (isRejected(d)) return 'Solicitud rechazada';
    if (isHoliday(d)) return 'Festivo';
    if (isNonWorking(d)) return 'Día no laborable';
    return '';
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(e.currentTarget);
    setStep('start');
    // Initialize draft from current confirmed values
    setDraftStart(startDate);
    setDraftEnd(endDate);
    const baseDate = startDate ?? new Date();
    setLeftMonth(new Date(baseDate.getFullYear(), baseDate.getMonth(), 1));
  };

  const handleClose = () => {
    setAnchorEl(null);
    setStep('start');
    setDraftStart(null);
    setDraftEnd(null);
  };

  const handleApply = () => {
    onRangeChange(draftStart, draftEnd);
    handleClose();
  };

  const handleDayClick = (date: Date) => {
    if (isBlocked(date)) return;
    if (step === 'start') {
      setDraftStart(date);
      setDraftEnd(null);
      setStep('end');
    } else {
      if (isBefore(date, draftStart!)) {
        setDraftStart(date);
        setDraftEnd(draftStart);
      } else {
        setDraftEnd(date);
      }
      // Stay open — user must press Aplicar
    }
  };

  // Effective range for highlighting (draft only — no hover preview)
  const effectiveEnd = draftEnd;
  const rangeStart = draftStart;
  const rangeEnd = effectiveEnd;

  const isInRange = (date: Date): boolean => {
    if (!rangeStart || !rangeEnd) return false;
    const lo = isBefore(rangeStart, rangeEnd) ? rangeStart : rangeEnd;
    const hi = isAfter(rangeStart, rangeEnd) ? rangeStart : rangeEnd;
    return isWithinInterval(date, { start: lo, end: hi });
  };

  const isRangeStart = (date: Date): boolean => {
    if (!rangeStart) return false;
    const lo = rangeEnd && isBefore(rangeEnd, rangeStart) ? rangeEnd : rangeStart;
    return isSameDay(date, lo);
  };

  const isRangeEnd = (date: Date): boolean => {
    if (!rangeStart || !rangeEnd) return false;
    const hi = isBefore(rangeStart, rangeEnd) ? rangeEnd : rangeStart;
    return isSameDay(date, hi);
  };

  const renderDay = (props: PickersDayProps<Date>) => {
    const { day, outsideCurrentMonth, ...other } = props;
    if (outsideCurrentMonth) return <PickersDay {...other} day={day} outsideCurrentMonth />;

    const blocked = isBlocked(day);
    const holiday = isHoliday(day);
    const nonWorking = isNonWorking(day);
    const pending = isPending(day);
    const approved = isApproved(day);
    const rejected = isRejected(day);

    const inRange = isInRange(day);
    const isStart = isRangeStart(day);
    const isEnd = isRangeEnd(day);
    const isSelected = (draftStart && isSameDay(day, draftStart)) || (draftEnd ? isSameDay(day, draftEnd) : false);
    const isOnlyStart = step === 'end' && draftStart ? isSameDay(day, draftStart) && !draftEnd : false;

    // Dot color shown below the day number for marked days
    const dotColor: string | null =
      approved ? '#2e7d32' :
      pending  ? '#ed6c02' :
      rejected ? '#9e9e9e' :
      holiday  ? '#c62828' :
      null;

    const cell = (
      <Box
        key={day.toISOString()}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Range background strip
          bgcolor: !blocked && inRange && !isStart && !isEnd
            ? 'rgba(0, 176, 189, 0.15)'
            : 'transparent',
          ...(!blocked && isStart && draftEnd ? {
            background: 'linear-gradient(90deg, transparent 50%, rgba(0, 176, 189, 0.15) 50%)',
          } : {}),
          ...(!blocked && isEnd && draftStart && draftEnd && !isSameDay(draftStart, draftEnd) ? {
            background: 'linear-gradient(90deg, rgba(0, 176, 189, 0.15) 50%, transparent 50%)',
          } : {}),
        }}
        onMouseEnter={() => {}}
        onMouseLeave={() => {}}
      >
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={false}
          disabled={blocked}
          onClick={() => handleDayClick(day)}
          selected={!blocked && (!!isSelected || isOnlyStart)}
          sx={{
            borderRadius: '50%',
            // Non-working days (no other mark): dimmed
            ...(nonWorking && !dotColor ? { opacity: '0.38 !important' } : {}),
            // Blocked days: keep MUI disabled style but restore opacity for colored dots
            ...(blocked && dotColor ? { opacity: '1 !important', '&.Mui-disabled': { opacity: '1 !important' } } : {}),
            // Selected range endpoints
            ...(!blocked && (isStart || isEnd || isOnlyStart) ? {
              bgcolor: '#004254 !important',
              color: '#fff !important',
              fontWeight: 700,
              '&:hover': { bgcolor: '#003040 !important' },
            } : {}),
            // Days inside range (not endpoints)
            ...(!blocked && inRange && !isStart && !isEnd ? {
              bgcolor: 'transparent',
              '&:hover': { bgcolor: 'rgba(0, 66, 84, 0.1)' },
            } : {}),
          }}
        />
        {/* Colored dot below the day number */}
        {dotColor && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 2,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: dotColor,
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
    );

    if (blocked) {
      return (
        <Tooltip key={day.toISOString()} title={getBlockedTooltip(day)} arrow placement="top">
          <span>{cell}</span>
        </Tooltip>
      );
    }
    return cell;
  };

  const formatDisplay = (d: Date | null) =>
    d ? format(d, 'dd/MM/yyyy', { locale: es }) : '';

  const displayValue =
    startDate && endDate
      ? `${formatDisplay(startDate)} – ${formatDisplay(endDate)}`
      : startDate
      ? `${formatDisplay(startDate)} – …`
      : '';

  const stepLabel = step === 'start' ? 'Selecciona fecha de inicio' : 'Selecciona fecha de fin';

  return (
    <>
      <TextField
        fullWidth
        margin="normal"
        label="Fecha de Inicio – Fin"
        value={displayValue}
        onClick={handleOpen}
        inputProps={{ readOnly: true, style: { cursor: disabled ? 'default' : 'pointer' } }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleOpen}
                edge="end"
                disabled={disabled}
                tabIndex={-1}
                size="small"
              >
                <CalendarTodayIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        error={error}
        helperText={helperText}
        disabled={disabled}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 2,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          },
        }}
      >
        {/* Step instruction */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            mb: 1,
            color: '#004254',
            fontWeight: 600,
            fontSize: '0.8rem',
          }}
        >
          {stepLabel}
        </Typography>

        {/* Legend */}
        {blockedDays && (
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mb: 1, flexWrap: 'wrap' }}>
            {[
              { color: '#c62828', label: 'Festivo' },
              { color: '#bdbdbd', label: 'No laborable' },
              { color: '#ed6c02', label: 'Pendiente' },
              { color: '#2e7d32', label: 'Aprobada' },
              { color: '#9e9e9e', label: 'Rechazada' },
            ].map(({ color, label }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>{label}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Two calendars side by side */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>

          {/* ── LEFT CALENDAR ── */}
          <Box sx={{ width: 280 }}>
            {/* Custom header: ← | Mes Año | (spacer) */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pb: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setLeftMonth(subMonths(leftMonth, 1))}
                disabled={minDate ? leftMonth <= new Date(minDate.getFullYear(), minDate.getMonth(), 1) : false}
              >
                <ChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{ flex: 1, textAlign: 'center', fontWeight: 600, textTransform: 'capitalize' }}
              >
                {format(leftMonth, 'MMMM yyyy', { locale: es })}
              </Typography>
              {/* Spacer to balance the left arrow */}
              <Box sx={{ width: 34 }} />
            </Box>
            <DateCalendar
              value={leftMonth}
              onChange={() => {}}
              reduceAnimations
              slots={{ day: renderDay as any }}
              slotProps={{ calendarHeader: { sx: { display: 'none' } } as any }}
              sx={{
                width: 280,
                height: 'auto',
                mt: 0,
                mb: 0,
                '& .MuiPickersCalendarHeader-root': { display: 'none' },
                '& .MuiDayCalendar-root': { mt: 0 },
                '& .MuiPickersSlideTransition-root': {
                  minHeight: '240px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  overflow: 'hidden',
                },
                '& .MuiDayCalendar-monthContainer': {
                  position: 'static',
                  width: '100%',
                },
              }}
            />
          </Box>

          {/* ── RIGHT CALENDAR ── */}
          <Box sx={{ width: 280 }}>
            {/* Custom header: (spacer) | Mes Año | → */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pb: 0.5 }}>
              {/* Spacer to balance the right arrow */}
              <Box sx={{ width: 34 }} />
              <Typography
                variant="body2"
                sx={{ flex: 1, textAlign: 'center', fontWeight: 600, textTransform: 'capitalize' }}
              >
                {format(rightMonth, 'MMMM yyyy', { locale: es })}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setLeftMonth(addMonths(leftMonth, 1))}
                disabled={maxDate ? rightMonth >= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1) : false}
              >
                <ChevronRight />
              </IconButton>
            </Box>
            <DateCalendar
              value={rightMonth}
              onChange={() => {}}
              onMonthChange={(newMonth) => {
                setLeftMonth(new Date(newMonth.getFullYear(), newMonth.getMonth() - 1, 1));
              }}
              minDate={minDate}
              maxDate={maxDate}
              reduceAnimations
              slots={{ day: renderDay as any }}
              slotProps={{ calendarHeader: { sx: { display: 'none' } } as any }}
              sx={{
                width: 280,
                height: 'auto',
                mt: 0,
                mb: 0,
                '& .MuiPickersCalendarHeader-root': { display: 'none' },
                '& .MuiDayCalendar-root': { mt: 0 },
                '& .MuiPickersSlideTransition-root': {
                  minHeight: '240px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  overflow: 'hidden',
                },
                '& .MuiDayCalendar-monthContainer': {
                  position: 'static',
                  width: '100%',
                },
              }}
            />
          </Box>

        </Box>

        {/* Footer: selection summary + action buttons */}
        <Divider sx={{ mt: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1.5, px: 1 }}>
          {/* Summary of current draft */}
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            {draftStart && draftEnd
              ? `${format(draftStart, 'dd/MM/yyyy', { locale: es })} – ${format(draftEnd, 'dd/MM/yyyy', { locale: es })}`
              : draftStart
              ? `Desde ${format(draftStart, 'dd/MM/yyyy', { locale: es })} — elige fin`
              : 'Selecciona fecha de inicio'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={handleClose}
              sx={{ color: 'text.secondary', textTransform: 'none' }}
            >
              Cancelar
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!draftStart || !draftEnd}
              onClick={handleApply}
              sx={{
                bgcolor: '#004254',
                color: '#fff',
                textTransform: 'none',
                '&:hover': { bgcolor: '#003040' },
                '&.Mui-disabled': { bgcolor: '#ccc' },
              }}
            >
              Aplicar
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangePickerField;
